/**
 * DEV-only equipment fixtures for Hero UI screenshots / manual tests.
 *
 * Never call from settlement, loot gen, or player-facing victory paths.
 * Session overlay uses Phaser registry / optional URL flag — does not write
 * fabricated gear into the real permanent save unless an explicit DEV action
 * opt-in is used (separate from screenshots).
 */

import {
  ITEM_DEFS,
  createEmptyEquippedSlots,
} from '../data/equipment';
import {
  addItemsToInventory,
  createItemInstance,
  equipItem,
  type InventoryState,
} from '../domain/equipment';
import type { PermanentSave } from './schema';
import { applyInventoryToPermanent, inventoryFromPermanent } from './schema';

/** Registry / URL key for session-only Hero fixture overlay. */
export const DEV_EQUIPMENT_FIXTURE_FLAG = 'devEquipmentFixture';

/** URL / registry value for low-vs-high sprite band verify overlay. */
export const DEV_SPRITE_VERIFY_FIXTURE = 'sprites';

/** Build a bag of all fixture defs (unique instance ids). */
export function createFixtureInventory(now = Date.now()): InventoryState {
  let inventory: InventoryState = {
    items: [],
    equipped: createEmptyEquippedSlots(),
  };
  const fixtures = ITEM_DEFS.filter((d) => d.fixture);
  const instances = fixtures.map((def, i) =>
    createItemInstance(def.id, {
      instanceId: `fixture_${def.id}_${i}`,
      rarity: def.defaultRarity ?? 'common',
      acquiredAt: now + i,
      source: 'dev_fixture',
    }),
  );
  const added = addItemsToInventory(inventory, instances);
  if (!added.ok) {
    return inventory;
  }
  inventory = added.inventory;

  // Equip a representative loadout for screenshots (2H → off-hand cleared).
  const ctx = { specialDieSlotUnlocked: true, classLevel: 60 };
  const sword = inventory.items.find((i) => i.defId === 'fix_greatsword_2h');
  const helmet = inventory.items.find((i) => i.defId === 'fix_helmet_leather');
  const chest = inventory.items.find((i) => i.defId === 'fix_chest_mail');
  const belt = inventory.items.find((i) => i.defId === 'fix_belt_simple');
  const boots = inventory.items.find((i) => i.defId === 'fix_boots_leather');
  const ringA = inventory.items.find((i) => i.defId === 'fix_ring_plain');
  const special = inventory.items.find((i) => i.defId === 'fix_special_die_blank');

  const steps: Array<{ id: string; slot: Parameters<typeof equipItem>[2] }> = [];
  if (helmet) steps.push({ id: helmet.instanceId, slot: 'helmet' });
  if (chest) steps.push({ id: chest.instanceId, slot: 'chest' });
  if (belt) steps.push({ id: belt.instanceId, slot: 'belt' });
  if (boots) steps.push({ id: boots.instanceId, slot: 'boots' });
  if (sword) steps.push({ id: sword.instanceId, slot: 'main_hand' });
  if (ringA) steps.push({ id: ringA.instanceId, slot: 'ring_1' });
  if (special) steps.push({ id: special.instanceId, slot: 'special_die' });

  for (const step of steps) {
    const result = equipItem(inventory, step.id, step.slot, ctx);
    if (result.ok) {
      inventory = result.inventory;
    }
  }

  // Second ring stays in the bag so filters show unequipped accessories.
  return inventory;
}

/**
 * DEV overlay highlighting low-band (C/U/R) vs high-band (E/L) sprite pools.
 * Session-only — never persist to real saves.
 */
export function createSpriteVerifyInventory(now = Date.now()): InventoryState {
  let inventory: InventoryState = {
    items: [],
    equipped: createEmptyEquippedSlots(),
  };
  const specs: Array<{
    defId: string;
    rarity: string;
    instanceId: string;
  }> = [
    { defId: 'fix_helmet_leather', rarity: 'common', instanceId: 'sv_helm_low' },
    { defId: 'fix_chest_mail', rarity: 'uncommon', instanceId: 'sv_chest_low' },
    { defId: 'fix_belt_simple', rarity: 'rare', instanceId: 'sv_belt_low' },
    { defId: 'fix_boots_leather', rarity: 'common', instanceId: 'sv_boots_low' },
    { defId: 'fix_sword_1h', rarity: 'common', instanceId: 'sv_sword_low' },
    { defId: 'fix_shield_wood', rarity: 'uncommon', instanceId: 'sv_shield_low' },
    { defId: 'fix_ring_plain', rarity: 'rare', instanceId: 'sv_ring_low' },
    // High band (epic / legendary) — same defs, different rarity → high pool art
    { defId: 'helm_iron', rarity: 'epic', instanceId: 'sv_helm_high' },
    { defId: 'chest_iron', rarity: 'legendary', instanceId: 'sv_chest_high' },
    { defId: 'belt_iron', rarity: 'epic', instanceId: 'sv_belt_high' },
    { defId: 'sword_iron', rarity: 'legendary', instanceId: 'sv_sword_high' },
    { defId: 'shield_iron', rarity: 'epic', instanceId: 'sv_shield_high' },
    { defId: 'ring_silver', rarity: 'legendary', instanceId: 'sv_ring_high' },
    { defId: 'greatsword_iron', rarity: 'epic', instanceId: 'sv_spear_high' },
    { defId: 'fix_special_die_blank', rarity: 'rare', instanceId: 'sv_die' },
  ];
  const instances = specs.map((s, i) =>
    createItemInstance(s.defId, {
      instanceId: s.instanceId,
      rarity: s.rarity,
      acquiredAt: now + i,
      source: 'dev_sprite_verify',
      itemLevel: s.rarity === 'legendary' || s.rarity === 'epic' ? 40 : 5,
    }),
  );
  const added = addItemsToInventory(inventory, instances);
  if (!added.ok) return inventory;
  inventory = added.inventory;

  const ctx = { specialDieSlotUnlocked: true, classLevel: 60 };
  const equipSteps: Array<{ id: string; slot: Parameters<typeof equipItem>[2] }> =
    [
      { id: 'sv_helm_high', slot: 'helmet' },
      { id: 'sv_chest_high', slot: 'chest' },
      { id: 'sv_belt_low', slot: 'belt' },
      { id: 'sv_boots_low', slot: 'boots' },
      { id: 'sv_sword_low', slot: 'main_hand' },
      { id: 'sv_shield_high', slot: 'off_hand' },
      { id: 'sv_ring_low', slot: 'ring_1' },
      { id: 'sv_ring_high', slot: 'ring_2' },
      { id: 'sv_die', slot: 'special_die' },
    ];
  for (const step of equipSteps) {
    const result = equipItem(inventory, step.id, step.slot, ctx);
    if (result.ok) inventory = result.inventory;
  }
  return inventory;
}

/**
 * Overlay fixture inventory onto a permanent snapshot WITHOUT mutating the
 * caller's object identity beyond a shallow clone. Caller must not persist
 * this as the real save for screenshots.
 */
export function overlayFixtureEquipment(
  permanent: PermanentSave,
  mode: 'default' | 'sprites' = 'default',
): PermanentSave {
  const fixture =
    mode === 'sprites' ? createSpriteVerifyInventory() : createFixtureInventory();
  return applyInventoryToPermanent(
    {
      ...permanent,
      specialDieSlotUnlocked: true,
    },
    fixture,
  );
}

/** Merge real inventory view (for non-fixture mode). */
export function permanentInventoryView(permanent: PermanentSave): InventoryState {
  return inventoryFromPermanent(permanent);
}

/**
 * DEV: append one copy of every lootable base def into the real inventory
 * (persisted). Does not auto-equip. Safe to press repeatedly (unique ids).
 */
export function grantDevEquipmentToPermanent(
  permanent: PermanentSave,
  now = Date.now(),
): { permanent: PermanentSave; addedCount: number } {
  const inventory = inventoryFromPermanent(permanent);
  const lootable = ITEM_DEFS.filter((d) => d.lootable && !d.fixture);
  const instances = lootable.map((def, i) =>
    createItemInstance(def.id, {
      instanceId: `dev_grant_${def.id}_${now}_${i}`,
      rarity: def.defaultRarity ?? 'common',
      acquiredAt: now + i,
      source: 'dev_grant',
      itemLevel: 1,
    }),
  );
  const added = addItemsToInventory(inventory, instances);
  if (!added.ok) {
    return { permanent, addedCount: 0 };
  }
  return {
    permanent: applyInventoryToPermanent(permanent, added.inventory),
    addedCount: instances.length,
  };
}

/**
 * DEV: append a dual-wield kit (sword + dagger) and equip it for M9-A Twin
 * Strike offer testing. Persists to the real save.
 */
export function grantDevDualWieldKitToPermanent(
  permanent: PermanentSave,
  now = Date.now(),
): { permanent: PermanentSave; ok: boolean; reason?: string } {
  let inventory = inventoryFromPermanent(permanent);
  const sword = createItemInstance('sword_iron', {
    instanceId: `dev_dw_sword_${now}`,
    rarity: 'common',
    acquiredAt: now,
    source: 'dev_grant',
    itemLevel: 1,
  });
  const dagger = createItemInstance('dagger_iron', {
    instanceId: `dev_dw_dagger_${now}`,
    rarity: 'common',
    acquiredAt: now + 1,
    source: 'dev_grant',
    itemLevel: 1,
  });
  const added = addItemsToInventory(inventory, [sword, dagger]);
  if (!added.ok) {
    return { permanent, ok: false, reason: 'duplicate_instance_id' };
  }
  inventory = added.inventory;
  const classLevel = permanent.classes.vanguard.level ?? 1;
  const ctx = {
    specialDieSlotUnlocked: permanent.specialDieSlotUnlocked,
    classId: 'vanguard' as const,
    classLevel,
  };
  const mh = equipItem(inventory, sword.instanceId, 'main_hand', ctx);
  if (!mh.ok) {
    return {
      permanent: applyInventoryToPermanent(permanent, inventory),
      ok: false,
      reason: mh.reason,
    };
  }
  inventory = mh.inventory;
  const oh = equipItem(inventory, dagger.instanceId, 'off_hand', ctx);
  if (!oh.ok) {
    return {
      permanent: applyInventoryToPermanent(permanent, inventory),
      ok: false,
      reason: oh.reason,
    };
  }
  return {
    permanent: applyInventoryToPermanent(permanent, oh.inventory),
    ok: true,
  };
}
